import { type IncludedFinalGrade } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";

import { useGetContractorBadgeVerification } from "../api/useGetContractorBadgeVerification";
import { GRADE_POLLING_INTERVAL_MS } from "../constants";
import { extractGradeFromVerificationData } from "../utils/extractGradeFromVerificationData";

interface UsePollForAiGradeParams {
  shiftId: string;
  enabled: boolean;
}

interface UsePollForAiGradeResult {
  gradeResult: IncludedFinalGrade | undefined;
}

/**
 * Polls for the badge verification grade after a successful submission.
 *
 * When `enabled` is true, fetches the grade query and re-fetches every
 * `GRADE_POLLING_INTERVAL_MS` until a grade arrives or `enabled` flips
 * to false (e.g. the GRADING step times out). Timeout handling lives in
 * `useBadgeVerificationPhotoFlow` as a hard `setTimeout` on the GRADING step.
 */
export function usePollForAiGrade(params: UsePollForAiGradeParams): UsePollForAiGradeResult {
  const { shiftId, enabled } = params;

  const { data: gradeQueryData } = useGetContractorBadgeVerification(shiftId, {
    enabled,
    includeFinalGrade: true,
    refetchInterval: (data) =>
      isDefined(extractGradeFromVerificationData(data)) ? false : GRADE_POLLING_INTERVAL_MS,
  });

  const gradeResult = extractGradeFromVerificationData(gradeQueryData);

  return { gradeResult };
}
